/**
 * Format a large numeric value (hashrate/byte count) into human-readable form
 * with SI prefixes: Z (10²¹), E (10¹⁸), P (10¹⁵), T (10¹²), G (10⁹), M (10⁶), K (10³)
 *
 * @param value - Raw number to format
 * @param suffix - Optional suffix appended after prefix (default: "")
 * @returns Formatted string like "1.15 E" or "1.23 PH/s" if suffix given
 *
 * @example
 * formatHashrate(1152921504606846976)  // "1.15 E"
 * formatHashrate(1234567890123)        // "1.23 T"
 * formatHashrate(500000)               // "0.5 M"
 * formatHashrate(null)                 // "0"
 */
export function formatHashrate(
  value: number | string | null | undefined,
  suffix: string = ""
): string {
  if (value === null || value === undefined) return `0${suffix ? ` ${suffix}` : ""}`;

  // API already returned a formatted string (e.g. "3.94 TH/s", "0 H/s") — pass through
  if (typeof value === "string" && /[a-zA-Z]/.test(value.trim())) {
    return value.trim();
  }

  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num) || num <= 0) return `0${suffix ? ` ${suffix}` : ""}`;

  const limits: { limit: number; prefix: string }[] = [
    { limit: 1_000_000_000_000_000_000_000, prefix: "Z" }, // 10²¹
    { limit: 1_000_000_000_000_000_000, prefix: "E" }, // 10¹⁸
    { limit: 1_000_000_000_000_000, prefix: "P" }, // 10¹⁵
    { limit: 1_000_000_000_000, prefix: "T" }, // 10¹²
    { limit: 1_000_000_000, prefix: "G" }, // 10⁹
    { limit: 1_000_000, prefix: "M" }, // 10⁶
    { limit: 1_000, prefix: "K" }, // 10³
  ];

  for (const { limit: l, prefix: p } of limits) {
    if (num >= l) {
      const formatted = (num / l).toFixed(2);
      // Remove trailing zeros: "1.50" -> "1.5", "2.00" -> "2"
      const clean = parseFloat(formatted).toString();
      return `${clean} ${p}${suffix}`;
    }
  }

  // Value is less than 1000, show as-is
  return `${num.toFixed(2)}${suffix ? ` ${suffix}` : ""}`;
}
